import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, map, ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { AppState, Timeout } from 'app/types';
import assertNever from 'app/utils/assert-never';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { BaseUser } from 'messages/websocket.messages';

import { PeerStats } from '../../preciate-stream/types';
import { PresentationService } from '../../services/presentation/presentation.service';
import { SceneService } from '../../services/scene.service';
import { VideoRoomService } from '../../services/video-room.service';
import { Player } from '../../social-pixi-canvas/player';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { getPresenterIds } from '../../state/app/selectors';
import { PresenterVideoQuality, ScalabilityMode, VideoCodecType } from '../../types/media';

const RESOURCE_PEER_INTERVAL_MS = 1000;
@Component({
  selector: 'peer-connection-debugger',
  templateUrl: './peer-connection-debugger.html',
  styleUrls: ['./peer-connection-debugger.scss'],
  standalone: false,
})
export class PeerConnectionDebuggerComponent extends Loggable implements OnInit, OnDestroy {
  private sceneService = inject(SceneService);
  private videoRoomService = inject(VideoRoomService);
  private store = inject<Store<AppState>>(Store);
  private presentationService = inject(PresentationService);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private peerInterval?: Timeout;
  private id?: string;

  public hdPresenterFlag = false;
  public isQualityOverridden = false;
  public overrideQuality = PresenterVideoQuality.QUALITY_STANDARD;
  public peers?: Player[];
  public readonly PresenterVideoQuality = PresenterVideoQuality;
  public readonly VideoCodec = VideoCodecType;
  public readonly ScalabilityMode = ScalabilityMode;
  public publishBitrate = 0;
  public readonly PUBLISHER_ID = 'publisher';
  public selectedInterval = 1000;
  public selectedPeer = 'publisher';
  public overrideCodec: VideoCodecType = VideoCodecType.VP9;
  public scalabilityMode = ScalabilityMode.NONE;
  public spatialLayer = 2;
  public temporalLayer = 2;
  private gallery?: User[] | BaseUser[];

  public constructor() {
    super();

    this.setDisplayName('DebuggerPeerConnectionComponent');

    // get gallery and presenter ids
    combineLatest([this.store.select(socialAppSelectors.getGallery), this.store.select(getPresenterIds)])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([gallery, presenterIds]) => {
        this.gallery = [
          ...(gallery as User[]),
          ...(presenterIds.map((p) => this.sceneService.getPlayer(p)).filter((p) => p !== undefined) as User[]),
        ];
        this.getAllPeers();
      });

    // get all peers in mingle mode
    this.sceneService?.sceneManager?.participantsCountChange.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.getAllPeers();
    });

    this.store
      .select(socialAppSelectors.getPublishVideoBitrate)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((bitrate) => (this.publishBitrate = bitrate));

    this.store
      .select(socialAppSelectors.getPresenterQualityOverride)
      .pipe(
        map((quality) => {
          this.overrideQuality = quality ?? PresenterVideoQuality.QUALITY_STANDARD;
          return !!quality;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((isQualityOverridden) => (this.isQualityOverridden = isQualityOverridden));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.hd_presenter)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hd_presenter) => {
        this.hdPresenterFlag = hd_presenter;
      });
  }

  public ngOnInit(): void {
    this.getPeerConnectionStats();
  }

  public getAllPeers(): void {
    // mingle mode
    if (!this.presentationService.isPresentationActive) {
      this.peers = this.sceneService.getAllParticipants().filter((player) => player.visible);
      // presentation mode
    } else {
      this.peers = this.gallery
        ?.map((gu) => {
          return this.sceneService.getPlayer(gu.userId);
        })
        .filter((player) => player !== undefined) as Player[];
    }
  }

  public getPeerConnectionStats() {
    this.videoRoomService?.startPeerConnectionStats('PeerConnectionDebugger', RESOURCE_PEER_INTERVAL_MS, this.id);
  }

  public onSelectionChange(id: string) {
    this.warn('onSelectionChange', id);
    this.id = id === this.PUBLISHER_ID ? '' : id;
    if (id !== this.PUBLISHER_ID) {
      const { spatialLayer, temporalLayer } = this.videoRoomService.getSVCLayerSelection(id);
      this.spatialLayer = spatialLayer;
      this.temporalLayer = temporalLayer;
    }
    this.getPeerConnectionStats();
  }

  public onQualityOverrideToggleChange(checked: boolean) {
    this.logUserAction('QualityOverrideToggle', 'change', { checked });
    if (checked) {
      this.store.dispatch(socialAppActions.OVERRIDE_QUALITY({ quality: this.overrideQuality }));
    } else {
      this.store.dispatch(socialAppActions.CLEAR_QUALITY_OVERRIDE());
    }
  }

  public onQualityOverride(value: PresenterVideoQuality) {
    this.logUserAction('QualityOverrideDropdown', 'click', { value });
    switch (value) {
      case PresenterVideoQuality.QUALITY_STANDARD:
      case PresenterVideoQuality.QUALITY_720P:
      case PresenterVideoQuality.QUALITY_1080P: {
        this.store.dispatch(socialAppActions.OVERRIDE_QUALITY({ quality: value }));
        break;
      }
      default: {
        assertNever(value);
      }
    }
  }

  public async onCodecOverride(value: VideoCodecType): Promise<void> {
    await this.videoRoomService.setCodecOverride(value);
  }

  public async onScalabilityModeOverride(value: ScalabilityMode): Promise<void> {
    this.scalabilityMode = value;
    await this.videoRoomService.setScalabilityMode(value);
  }

  public onTemporalLayerChange(value: number): void {
    this.temporalLayer = value;
    this.videoRoomService.selectTemporalLayer(this.selectedPeer, value);
  }

  public onSpatialLayerChange(value: number): void {
    this.spatialLayer = value;
    this.videoRoomService.selectSpatialLayer(this.selectedPeer, value);
  }

  public get isPublishPeerSelected(): boolean {
    return this.selectedPeer === this.PUBLISHER_ID;
  }

  public get peerStats(): PeerStats | undefined {
    return this.videoRoomService.peerStats;
  }

  public get statsPeerConnection(): RTCPeerConnection | undefined {
    return this.videoRoomService.statsPeer;
  }

  public ngOnDestroy(): void {
    if (this.peerInterval) {
      clearInterval(this.peerInterval);
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
